import React, { useEffect, useState } from "react";
import {
	BackgroundContainer,
	Container,
	Inner,
	LogoContainer,
} from "./styles.js";
import { Typo } from "../../components/layout/theme/index.js";
import { Button, Form, Input } from "antd";
import { useNavigate } from "react-router-dom";
import { get_companies } from "../../api/endpoints/quotations.js";
import Image from "../../components/common/image/Image.js";
import { useFilter } from "../../contexts/FilterContext.js";
import { useReport } from "../../contexts/ReportContext.js";
import { get_car_data_by_patent } from "../../api/endpoints/cars.js";

const Home = ({ isMobile }) => {
	const navigate = useNavigate();
	const { setStep1Car, setCarData } = useFilter();
	const { updateReportForm } = useReport();
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();

	const patent = Form.useWatch("carPatent", form);

	// Función para formatear la patente: solo letras y números, letras en mayúsculas
	const formatPatent = (value) => {
		if (!value) return value;
		// Remover caracteres que no sean letras o números
		const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
		// Convertir a mayúsculas
		return cleaned.toUpperCase();
	};

	// Manejar cambios en el campo de patente
	const handlePatentChange = (e) => {
		const formatted = formatPatent(e.target.value);
		form.setFieldValue('carPatent', formatted);
	};

	useEffect(() => {
		const fetch_companies = () => {
			const res = get_companies();
			setCompanies(res);
		};
		fetch_companies();
	}, []);

	const handleButtonHomeClick = async () => {
		if (!patent || patent.trim() === "") {
			// Si no hay patente, ir al flujo manual
			setStep1Car(0);
			navigate("/quotationProcess");
			return;
		}

		setLoading(true);

		try {
			// Intentar obtener datos del vehículo usando la API cardata
			const carDataResponse = await get_car_data_by_patent(patent.trim());

			if (carDataResponse.status === "success" && carDataResponse.codia) {
				// Éxito: cargar datos completos del vehículo
				const vehicleData = {
					// Datos básicos del vehículo
					patent: carDataResponse.dominio,
					brandName: carDataResponse.marca,
					modelName: carDataResponse.modelo,
					codia: carDataResponse.codia,
					// Get year by car data search
					year: carDataResponse.source_data.agd_online.anioModelo,

					// Datos de InfoAuto si están disponibles
					...(carDataResponse.source_data?.infoauto?.brand && {
						brandId: carDataResponse.source_data.infoauto.brand.id,
						brandLogo: carDataResponse.source_data.infoauto.brand.logo_url,
					}),

					...(carDataResponse.source_data?.infoauto?.model && {
						modelId: carDataResponse.source_data.infoauto.model.codia,
						description: carDataResponse.source_data.infoauto.model.description,
						photo_url: carDataResponse.source_data.infoauto.model.photo_url,
						features: carDataResponse.source_data.infoauto.model.features,
						prices_from: carDataResponse.source_data.infoauto.model.prices_from,
						prices_to: carDataResponse.source_data.infoauto.model.prices_to,
					}),

					// Información de búsqueda
					search_info: carDataResponse.search_info,

					// Datos completos para referencia
					full_response: carDataResponse,
				};

				setCarData(vehicleData);

				// Guardar titularActual y automotor en el contexto en una sola llamada
				// para evitar que una llamada sobrescriba a la otra
				const reportData = {};

				if (carDataResponse.titular_actual) {
					console.log("Guardando titular_actual en contexto:", carDataResponse.titular_actual);
					reportData.titularActual = carDataResponse.titular_actual;
				}

				if (carDataResponse.automotor) {
					console.log("Guardando automotor en contexto:", carDataResponse.automotor);
					reportData.automotor = carDataResponse.automotor;
				}

				// Llamar updateReportForm una sola vez con ambos valores
				if (Object.keys(reportData).length > 0) {
					updateReportForm(reportData);
				}

				// Ir al paso de confirmación de datos del vehículo (step1Car = 1) para mostrar los detalles
				setStep1Car(1);
				navigate("/quotationProcess");
			} else {
				// Respuesta exitosa pero sin CODIA - ir al flujo manual
				console.warn("API response successful but no CODIA found:", carDataResponse);
				setStep1Car(0);
				navigate("/quotationProcess");
			}
		} catch (error) {
			// Error en la API - ir al flujo manual
			console.error("Error fetching car data:", error);

			// Ir al flujo manual de búsqueda
			setStep1Car(0);
			navigate("/quotationProcess");
		} finally {
			setLoading(false);
		}
	};

	const handleForgottenPatent = () => {
		setStep1Car(0);
		navigate("/quotationProcess");
	};

	return (
		<Form form={form} name="home-form">
			<Inner>
				<Typo type="secondary" level={5} style={{ textAlign: "center" }}>
					En 3 simples pasos
				</Typo>
				<Typo level={2} style={{ textAlign: "center" }}>
					¡Encontra la cobertura para tu auto!
				</Typo>
				<BackgroundContainer isMobile={isMobile}>
					<Container>
						<Form.Item name="carPatent" style={{ marginBottom: 0 }}>
							<Input
								placeholder="Patente del auto"
								onChange={handlePatentChange}
								maxLength={8}
							/>
						</Form.Item>
						<Button
							onClick={handleButtonHomeClick}
							type="primary"
							loading={loading}
							disabled={loading}
						>
							{loading ? "Buscando..." : "Cotizar"}
						</Button>
					</Container>
					<Container
						style={{ cursor: "pointer" }}
						onClick={handleForgottenPatent}
					>
						<Typo type="secondary">No recuerdo la patente...</Typo>
					</Container>
				</BackgroundContainer>
				<LogoContainer>
					{companies.length > 0 &&
						companies.map((c, idx) => {
							return <Image width={90} key={idx} src={c.logo} alt={c.name} />;
						})}
				</LogoContainer>
			</Inner>
		</Form>
	);
};

export default Home;
